// errorElement for the authenticated app routes. Replaces React Router's raw
// dev error page with a calm screen. This side is baker-only, so the technical
// message is shown (muted) to help debugging, plus a reload.

import { lazy, Suspense } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { NEW_UI } from "@/features/orders/localPreview";

const LocalError =
  NEW_UI
    ? lazy(() =>
        import("./LocalRouteErrorScreen").then((module) => ({
          default: module.LocalRouteErrorScreen,
        })),
      )
    : null;

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error !== null && typeof error === "object" && "statusText" in error) {
    const statusText = error.statusText;
    if (typeof statusText === "string") return statusText;
  }
  return "Unknown error";
}

export function RouteErrorScreen(): React.ReactElement {
  const error = useRouteError();
  console.error("Route error", error);

  if (LocalError) {
    const missing = isRouteErrorResponse(error) && error.status === 404;
    return (
      <Suspense fallback={<div role="status">Opening recovery options…</div>}>
        <LocalError missing={missing} message={errorMessage(error)} />
      </Suspense>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Stack spacing={2} sx={{ alignItems: "center", maxWidth: 480, textAlign: "center" }}>
        <Typography variant="h4">Something went wrong</Typography>
        <Typography variant="body1" color="text.secondary">
          An unexpected error occurred. Try reloading the page.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ wordBreak: "break-word", opacity: 0.75 }}
        >
          {errorMessage(error)}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </Button>
      </Stack>
    </Box>
  );
}
